import { useMemo } from 'react';

export default function MapView({ activities = [], restaurants = [], destinationCity }) {
  const neighborhoods = useMemo(() => {
    const groups = {};

    // Group restaurants by neighborhood
    restaurants.forEach(r => {
      const hood = r.neighborhood || 'Other';
      if (!groups[hood]) groups[hood] = { activities: [], restaurants: [] };
      groups[hood].restaurants.push(r);
    });

    // Group activities — use last word of tip as a rough area, or "Activities"
    activities.forEach(a => {
      let hood = 'Activities';
      if (a.tip) {
        const words = a.tip.trim().split(/\s+/);
        if (words.length > 0) hood = words[words.length - 1];
      }
      if (!groups[hood]) groups[hood] = { activities: [], restaurants: [] };
      groups[hood].activities.push(a);
    });

    return Object.entries(groups).map(([name, items]) => ({
      name,
      activities: items.activities,
      restaurants: items.restaurants,
      totalItems: items.activities.length + items.restaurants.length,
      totalCost:
        items.activities.reduce((s, a) => s + (a.price_for_two || 0), 0) +
        items.restaurants.reduce((s, r) => s + (r.avg_meal_for_two || 0), 0),
    }));
  }, [activities, restaurants]);

  if (neighborhoods.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-white">By Neighborhood</h3>
        {destinationCity && <span className="text-xs text-brand-ink/55">{destinationCity}</span>}
      </div>
      <div className="text-xs text-brand-ink/55 mb-2">
        Spots grouped by area — plan your days around neighborhoods to save time.
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {neighborhoods.map(hood => (
          <div key={hood.name} className="bg-navy-light rounded-xl border border-brand-ink/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-sm text-white">{hood.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-brand-ink/55">{hood.totalItems} spot{hood.totalItems !== 1 ? 's' : ''}</span>
                {hood.totalCost > 0 && (
                  <span className="text-[10px] text-gold">${hood.totalCost}</span>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              {hood.activities.map((a, i) => (
                <div key={`a-${i}`} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" />
                    <span className="text-xs text-brand-ink/80">{a.name}</span>
                  </div>
                  {a.price_for_two > 0 && <span className="text-[11px] text-gold">${a.price_for_two}</span>}
                </div>
              ))}
              {hood.restaurants.map((r, i) => (
                <div key={`r-${i}`} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    <span className="text-xs text-brand-ink/80">{r.name}</span>
                  </div>
                  {r.avg_meal_for_two > 0 && <span className="text-[11px] text-gold">${r.avg_meal_for_two}</span>}
                </div>
              ))}
            </div>

            {hood.totalItems >= 2 && (
              <div className="mt-3 pt-2 border-t border-brand-ink/5">
                <div className="text-[11px] text-teal">
                  These {hood.totalItems} spots are all in {hood.name} — do them together on the same day
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 pt-1">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-teal" />
          <span className="text-[10px] text-brand-ink/55">Activity</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-gold" />
          <span className="text-[10px] text-brand-ink/55">Restaurant</span>
        </div>
      </div>
    </div>
  );
}
